import type {
  SpinRequest,
  SpinResponse,
  RoundProvider,
  SpinResult,
  RejectedSpinResponse,
  GambleRequest,
  GambleResponse,
  GambleOfferDetails,
} from "@slot/client-engine";

import type {
  PresentationCompleteRequest,
  PresentationCompleteResponse,
} from "@slot/client-engine";

import type {
  GambleCompleteRequest,
  GambleCompleteResponse,
} from "@slot/client-engine";

import type {
  ScriptedFixtureCatalog,
  ScriptedFixtureGroup,
} from "./ScriptedFixtureCatalog";

import type { GambleFlow, GambleStep } from "./fixtures/GambleFlow";

class FixtureSequence {
  private readonly fixtures: SpinResponse[];
  private readonly cycle: boolean;
  private index: number = 0;

  public constructor(fixtures: SpinResponse[], input?: { cycle?: boolean }) {
    if (!fixtures.length) {
      throw new Error("FixtureSequence requires at least one fixture.");
    }

    this.fixtures = fixtures;
    this.cycle = input?.cycle ?? true;
  }

  public next(): SpinResponse {
    const selected = this.fixtures[this.index];

    if (this.cycle) {
      this.index = (this.index + 1) % this.fixtures.length;
    } else {
      this.index = Math.min(this.index + 1, this.fixtures.length - 1);
    }

    return selected;
  }

  public reset(index: number = 0): void {
    this.index = Math.max(0, Math.min(index, this.fixtures.length - 1));
  }
}

export class ScriptedRoundProvider implements RoundProvider {
  private readonly baseSeq: FixtureSequence;
  /** The flat base channel: the catalog's sequence, or the dev playlist that replaces it (`setBaseSource`). */
  private baseSource: { next(): SpinResponse };
  private readonly featureSeq: FixtureSequence;
  /** Per-purchase pools keyed by requestedFeatureIdentifier (fallback: featureSeq). */
  private readonly featureSeqById = new Map<string, FixtureSequence>();
  /** Per-spin-mode base pools keyed by requestedFeatureModeIdentifier (fallback: baseSeq). */
  private readonly baseSeqByModeId = new Map<string, FixtureSequence>();

  private activeGroup: ScriptedFixtureGroup;
  private simulatedRoundCounter = 0;
  private requestCounter = 0;
  private simulatedLatencyMs: number;

  private lastSpinByRoundId = new Map<string, SpinResponse>();

  private readonly rejectLowBalanceForTesting: boolean;
  private readonly rejectedBalanceAmount: string;
  private readonly rejectedCurrencyCode: string;
  private readonly rejectOnRequestNumbers: Set<number>;

  // Simulated server-side gamble tracking (authoritative per round).
  // Used only as defaults when a fixture does not declare its own offer.
  private readonly defaultGambleMaxAttempts = 5;
  private readonly defaultGambleCeilingMinor = "1000000";
  private gambleAttemptsByRoundId = new Map<string, number>();
  private gambleMaxAttemptsByRoundId = new Map<string, number>();
  private gambleCeilingByRoundId = new Map<string, string>();
  private gambleOptionsByRoundId = new Map<string, GambleOfferDetails["options"]>();
  private gambleTypeByRoundId = new Map<string, string>();
  private gambleOutcomesByRoundId = new Map<string, GambleStep[]>();
  private lastGambleBalanceByRoundId = new Map<string, string>();

  /**
   * Round-robin cursor over the catalog's gamble flows. Each time a
   * round offers gamble, the next flow is copied and bound to the
   * round. Undefined when the catalog did not supply any flows.
   */
  private readonly gambleFlows: GambleFlow[];
  private gambleFlowCursor = 0;

  public constructor(
    catalog: ScriptedFixtureCatalog,
    opts?: {
      simulatedLatencyMs?: number;
      cycleThroughFixtures?: boolean;
      startGroup?: ScriptedFixtureGroup;

      // dev/testing only
      rejectLowBalanceForTesting?: boolean;
      rejectedBalanceAmount?: string;
      rejectedCurrencyCode?: string;
      rejectOnRequestNumbers?: number[];
    }
  ) {
    const cycle = opts?.cycleThroughFixtures ?? true;

    this.baseSeq = new FixtureSequence(catalog.baseFixtures, { cycle });
    this.baseSource = this.baseSeq;
    this.featureSeq = new FixtureSequence(catalog.featureFixtures, { cycle });
    for (const [featureId, fixtures] of Object.entries(catalog.featureFixturesById ?? {})) {
      if (fixtures.length > 0) this.featureSeqById.set(featureId, new FixtureSequence(fixtures, { cycle }));
    }
    for (const [modeId, fixtures] of Object.entries(catalog.baseFixturesByModeId ?? {})) {
      if (fixtures.length > 0) this.baseSeqByModeId.set(modeId, new FixtureSequence(fixtures, { cycle }));
    }
    this.gambleFlows = catalog.gambleFlows ?? [];

    this.simulatedLatencyMs = opts?.simulatedLatencyMs ?? 0;
    this.activeGroup = opts?.startGroup ?? "base";

    this.rejectLowBalanceForTesting = opts?.rejectLowBalanceForTesting ?? false;
    this.rejectedBalanceAmount = opts?.rejectedBalanceAmount ?? "420";
    this.rejectedCurrencyCode = opts?.rejectedCurrencyCode ?? "AMD";
    this.rejectOnRequestNumbers = new Set(opts?.rejectOnRequestNumbers ?? []);
  }

  /** The round-trip delay every following request waits (dev). */
  public setSimulatedLatency(ms: number): void {
    this.simulatedLatencyMs = Math.max(0, ms);
  }

  public getActiveGroup(): ScriptedFixtureGroup {
    return this.activeGroup;
  }

  public setActiveGroup(group: ScriptedFixtureGroup): void {
    this.activeGroup = group;
  }

  public reset(group: ScriptedFixtureGroup, index: number = 0): void {
    if (group === "base") this.baseSeq.reset(index);
    else this.featureSeq.reset(index);
  }

  /**
   * What the flat base channel serves from now on (the dev playlist, `FixtureRoundProvider`); null
   * returns to the catalog's sequence, which kept its place. The keyed pools still win: a purchased
   * feature and an active feature mode serve their own fixtures.
   */
  public setBaseSource(source: { next(): SpinResponse } | null): void {
    this.baseSource = source ?? this.baseSeq;
  }

  private async sleepIfNeeded(): Promise<void> {
    if (this.simulatedLatencyMs <= 0) return;
    await new Promise((r) => setTimeout(r, this.simulatedLatencyMs));
  }

  /**
   * Simulated server routing:
   * - feature group → the requested PURCHASE id's pool when one exists, else the flat
   *   feature list (so distinct bonus buys can serve distinct sessions);
   * - base group → the active SPIN MODE's pool when one exists, else the flat base list or the
   *   source that replaces it (so a persistent mode changes what normal spins serve).
   * `reset(group, index)` only rewinds the two flat sequences — keyed pools keep their cursor.
   */
  private nextFixtureForRequest(req: SpinRequest, group: ScriptedFixtureGroup): SpinResponse {
    if (group === "feature") {
      const featureId = req.requestedFeatureIdentifier;
      const keyed = featureId ? this.featureSeqById.get(featureId) : undefined;
      return (keyed ?? this.featureSeq).next();
    }
    const modeId = req.requestedFeatureModeIdentifier;
    const keyedBase = modeId ? this.baseSeqByModeId.get(modeId) : undefined;
    return (keyedBase ?? this.baseSource).next();
  }

  private resolveGroupForRequest(req: SpinRequest): ScriptedFixtureGroup {
    if (req.requestedFeatureIdentifier && req.requestedFeatureIdentifier.length > 0) {
      return "feature";
    }

    return this.activeGroup;
  }

  private stampSimulatedRoundId(spin: SpinResponse): SpinResponse {
    this.simulatedRoundCounter += 1;

    const baseRoundId = spin.roundSnapshot.roundIdentifier;
    const nextRoundId = `${baseRoundId}#sim-${this.simulatedRoundCounter}`;

    return {
      ...spin,
      roundSnapshot: {
        ...spin.roundSnapshot,
        roundIdentifier: nextRoundId,
      },
    };
  }

  private buildRejectedLowBalanceResponse(): RejectedSpinResponse {
    return {
      serverTimeIsoString: new Date().toISOString(),
      accountBalanceSnapshot: {
        currencyCode: this.rejectedCurrencyCode,
        balanceAmount: this.rejectedBalanceAmount,
      },
      responseMetadata: {
        statusCode: 402,
        statusMessage: "insufficient_funds",
      },
    };
  }

  public async requestRoundSnapshot(req: SpinRequest): Promise<SpinResult> {
    await this.sleepIfNeeded();

    this.requestCounter += 1;

    if (
      this.rejectLowBalanceForTesting &&
      this.rejectOnRequestNumbers.has(this.requestCounter)
    ) {
      return this.buildRejectedLowBalanceResponse();
    }

    const group = this.resolveGroupForRequest(req);
    const baseSpin = this.nextFixtureForRequest(req, group);
    const stamped = this.stampSimulatedRoundId(baseSpin);
    const spin = this.attachGambleOfferIfApplicable(stamped);

    const roundId = spin.roundSnapshot.roundIdentifier;
    this.lastSpinByRoundId.set(roundId, spin);

    return spin;
  }

  /**
   * Simulated server authority: if a fixture's possibleClientActions
   * includes "gamble", register per-round gamble limits and stamp a
   * GambleOfferDetails onto the round snapshot (unless the fixture
   * declared its own — in which case those values are captured for
   * use in chained gamble responses). The stamped offer's
   * currentWinAmountMinor is the round's win, the last non-empty
   * accumulatedWinAmountAfterEvent: the amount the settlement credits
   * and the only stake the platform backend accepts (decision 29).
   */
  private attachGambleOfferIfApplicable(spin: SpinResponse): SpinResponse {
    const actions = spin.roundSnapshot.possibleClientActions ?? [];
    if (!actions.includes("gamble")) return spin;

    const roundId = spin.roundSnapshot.roundIdentifier;
    const fixtureOffer = spin.roundSnapshot.gambleOfferDetails;

    const defaultOptions: GambleOfferDetails["options"] = [
      { optionId: "color_red", label: "Red", multiplier: "2", probability: "0.50", group: "color" },
      { optionId: "color_black", label: "Black", multiplier: "2", probability: "0.50", group: "color" },
    ];

    const maxAttempts =
      fixtureOffer?.maxAttemptsRemaining !== undefined
        ? fixtureOffer.maxAttemptsRemaining
        : this.defaultGambleMaxAttempts;
    const ceiling =
      fixtureOffer?.gambleCeilingMinor ?? this.defaultGambleCeilingMinor;
    const options =
      fixtureOffer?.options && fixtureOffer.options.length > 0
        ? fixtureOffer.options
        : defaultOptions;
    const gambleType = fixtureOffer?.gambleType ?? "post_win";

    this.gambleAttemptsByRoundId.set(roundId, 0);
    this.gambleMaxAttemptsByRoundId.set(roundId, maxAttempts);
    this.gambleCeilingByRoundId.set(roundId, ceiling);
    this.gambleOptionsByRoundId.set(roundId, options);
    this.gambleTypeByRoundId.set(roundId, gambleType);

    // Bind the next gamble flow from the catalog to this round.
    // Flows are cycled round-robin so repeated tests rotate scenarios.
    if (this.gambleFlows.length === 0) {
      throw new Error(
        `ScriptedRoundProvider: round "${roundId}" declares "gamble" in possibleClientActions but the fixture catalog has no gambleFlows configured.`
      );
    }
    const flow = this.gambleFlows[this.gambleFlowCursor % this.gambleFlows.length];
    this.gambleFlowCursor = (this.gambleFlowCursor + 1) % this.gambleFlows.length;
    // Copy so we can mutate (shift) without disturbing the source flow.
    this.gambleOutcomesByRoundId.set(roundId, flow.slice());

    // If the fixture already provided an offer, pass it through untouched.
    if (fixtureOffer) return spin;

    // The last non-empty accumulatedWinAmountAfterEvent (`findLast` is past the ES2022 lib).
    const roundWinMinor =
      [...spin.roundSnapshot.orderedEvents].reverse().find((event) => event.accumulatedWinAmountAfterEvent)?.accumulatedWinAmountAfterEvent ?? "0";

    return {
      ...spin,
      roundSnapshot: {
        ...spin.roundSnapshot,
        gambleOfferDetails: this.buildSimulatedGambleOffer({
          roundIdentifier: roundId,
          currentWinAmountMinor: roundWinMinor,
          attemptsUsed: 0,
        }),
      },
    };
  }

  private buildSimulatedGambleOffer(input: {
    roundIdentifier: string;
    currentWinAmountMinor: string;
    attemptsUsed: number;
  }): GambleOfferDetails {
    const maxAttempts =
      this.gambleMaxAttemptsByRoundId.get(input.roundIdentifier) ??
      this.defaultGambleMaxAttempts;
    const ceiling =
      this.gambleCeilingByRoundId.get(input.roundIdentifier) ??
      this.defaultGambleCeilingMinor;
    const options =
      this.gambleOptionsByRoundId.get(input.roundIdentifier) ?? [];
    const gambleType =
      this.gambleTypeByRoundId.get(input.roundIdentifier) ?? "post_win";

    return {
      gambleType,
      currentWinAmountMinor: input.currentWinAmountMinor,
      options,
      maxAttemptsRemaining: Math.max(0, maxAttempts - input.attemptsUsed),
      gambleCeilingMinor: ceiling,
    };
  }

  public async acknowledgePresentationComplete(
    req: PresentationCompleteRequest
  ): Promise<PresentationCompleteResponse> {
    await this.sleepIfNeeded();

    const spin = this.lastSpinByRoundId.get(req.roundIdentifier);

    const accountBalanceSnapshot = spin?.accountBalanceSnapshot ?? {
      balanceAmount: this.rejectedBalanceAmount,
      currencyCode: this.rejectedCurrencyCode,
    };

    const settlement: PresentationCompleteResponse = {
      serverTimeIsoString: new Date().toISOString(),
      accountBalanceSnapshot,
      roundIdentifier: req.roundIdentifier,
    };

    return settlement;
  }

  /**
   * Simulated gamble: consumes one step from the fixture-authored
   * outcome sequence per call. When the sequence is exhausted or a
   * step marks `terminatesFlow`, the sim stops offering "gamble".
   */
  public async requestGamble(request: GambleRequest): Promise<GambleResponse> {
    await this.sleepIfNeeded();

    const staked = BigInt(request.stakedAmountMinor);

    if (request.gambleAction === "collect") {
      // Collect is a client-signaled flow exit, not a fixture step. The
      // authoritative balance on collect is simply the balance already
      // carried by the last step. If the player collects without having
      // gambled first, fall back to the pre-gamble base balance plus
      // the staked (un-gambled) win.
      const lastStepBalance = this.lastGambleBalanceByRoundId.get(
        request.roundIdentifier
      );
      const balanceAmount =
        lastStepBalance ??
        this.computeGambleBalance(request.roundIdentifier, staked).toString();
      this.lastGambleBalanceByRoundId.set(request.roundIdentifier, balanceAmount);
      return {
        roundIdentifier: request.roundIdentifier,
        gambleOutcome: "win",
        previousWinAmountMinor: request.stakedAmountMinor,
        newWinAmountMinor: request.stakedAmountMinor,
        possibleClientActions: [],
        accountBalanceSnapshot: { balanceAmount },
      };
    }

    // Deterministic fixture-driven outcome.
    const outcomes = this.gambleOutcomesByRoundId.get(request.roundIdentifier);
    if (!outcomes || outcomes.length === 0) {
      throw new Error(
        `ScriptedRoundProvider.requestGamble: no gamble outcomes registered for round "${request.roundIdentifier}".`
      );
    }

    const step = outcomes.shift()!;
    const isWin = step.outcome === "win";
    const multiplier = step.multiplier ?? "2";

    const attemptsUsed = (this.gambleAttemptsByRoundId.get(request.roundIdentifier) ?? 0) + 1;
    this.gambleAttemptsByRoundId.set(request.roundIdentifier, attemptsUsed);

    const newWin = isWin ? (staked * BigInt(multiplier)).toString() : "0";
    // Fixture-authoritative balance: the step carries the post-step
    // balance exactly as a real backend would ship it.
    const balanceAmount = step.balanceAmountMinor;
    this.lastGambleBalanceByRoundId.set(request.roundIdentifier, balanceAmount);

    const maxAttempts =
      this.gambleMaxAttemptsByRoundId.get(request.roundIdentifier) ??
      this.defaultGambleMaxAttempts;

    const attemptsRemaining = Math.max(0, maxAttempts - attemptsUsed);
    const sequenceExhausted = outcomes.length === 0;
    const canGambleAgain =
      isWin &&
      !step.terminatesFlow &&
      !sequenceExhausted &&
      attemptsRemaining > 0;

    const serverChosenValue =
      step.serverChosenValue ?? (isWin ? "red" : "black");

    return {
      roundIdentifier: request.roundIdentifier,
      gambleOutcome: isWin ? "win" : "lose",
      previousWinAmountMinor: request.stakedAmountMinor,
      newWinAmountMinor: newWin,
      multiplierApplied: isWin ? multiplier : undefined,
      gambleOptionId: request.gambleOptionId,
      serverChosenValue,
      possibleClientActions: canGambleAgain ? ["gamble"] : [],
      gambleOfferDetails: canGambleAgain
        ? this.buildSimulatedGambleOffer({
            roundIdentifier: request.roundIdentifier,
            currentWinAmountMinor: newWin,
            attemptsUsed,
          })
        : undefined,
      accountBalanceSnapshot: { balanceAmount },
    };
  }

  /**
   * Mirrors `acknowledgePresentationComplete` for the gamble flow.
   * Returns the authoritative post-gamble balance (the last balance
   * computed by `requestGamble`, falling back to the post-bet-deduction
   * balance if the client acked without ever calling `requestGamble`)
   * and clears per-round gamble state.
   */
  public async acknowledgeGambleComplete(
    request: GambleCompleteRequest
  ): Promise<GambleCompleteResponse> {
    await this.sleepIfNeeded();

    const spin = this.lastSpinByRoundId.get(request.roundIdentifier);

    if (!spin) {
      throw new Error(
        `ScriptedRoundProvider.acknowledgeGambleComplete: unknown roundIdentifier "${request.roundIdentifier}".`
      );
    }

    const lastGambleBalance = this.lastGambleBalanceByRoundId.get(
      request.roundIdentifier
    );
    const spinBalance = spin.accountBalanceSnapshot;
    if (!spinBalance) {
      throw new Error(`ScriptedRoundProvider: spin ${request.roundIdentifier} has no accountBalanceSnapshot.`);
    }
    const balanceAmount = lastGambleBalance ?? spinBalance.balanceAmount;
    const currencyCode = spinBalance.currencyCode;

    this.clearGambleStateForRound(request.roundIdentifier);

    return {
      roundIdentifier: request.roundIdentifier,
      serverTimeIsoString: new Date().toISOString(),
      accountBalanceSnapshot: { balanceAmount, currencyCode },
    };
  }

  private clearGambleStateForRound(roundIdentifier: string): void {
    this.gambleAttemptsByRoundId.delete(roundIdentifier);
    this.gambleMaxAttemptsByRoundId.delete(roundIdentifier);
    this.gambleCeilingByRoundId.delete(roundIdentifier);
    this.gambleOptionsByRoundId.delete(roundIdentifier);
    this.gambleTypeByRoundId.delete(roundIdentifier);
    this.gambleOutcomesByRoundId.delete(roundIdentifier);
    this.lastGambleBalanceByRoundId.delete(roundIdentifier);
  }

  private computeGambleBalance(roundIdentifier: string, winAmountToCredit: bigint): bigint {
    const spin = this.lastSpinByRoundId.get(roundIdentifier);
    const baseBalance = BigInt(spin?.accountBalanceSnapshot?.balanceAmount ?? "0");
    return baseBalance + winAmountToCredit;
  }
}